/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useEffect, useState, useMemo } from 'react';
import Highcharts from 'highcharts/es-modules/masters/highcharts.src';
import "highcharts/es-modules/masters/modules/heatmap.src";
import HighchartsReact from 'highcharts-react-official';
import { Spinner } from 'react-bootstrap';
import { formatNumber } from '../../../../libs/misc';

interface LabourImpactHeatmapProps {
  data: any[];
}

const LabourImpactHeatmap: FC<LabourImpactHeatmapProps> = ({ data }) => {
  const [isLoading, setIsLoading] = useState(true);

  const formatCellNumber = (val: number) => {
    if (val === 0) return '-';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(val);
  };

  const interpolateColor = (color1: string, color2: string, ratio: number) => {
    const r1 = parseInt(color1.substring(1, 3), 16);
    const g1 = parseInt(color1.substring(3, 5), 16);
    const b1 = parseInt(color1.substring(5, 7), 16);
    const r2 = parseInt(color2.substring(1, 3), 16);
    const g2 = parseInt(color2.substring(3, 5), 16);
    const b2 = parseInt(color2.substring(5, 7), 16);
    const r = Math.round(r1 + (r2 - r1) * ratio);
    const g = Math.round(g1 + (g2 - g1) * ratio);
    const b = Math.round(b1 + (b2 - b1) * ratio);
    return `rgb(${r},${g},${b})`;
  };

  const options: any = useMemo(() => {
    if (!data || data.length === 0 || !data[0]) return {};

    const sectorCodes = data.map((row) => row.oecd_code);
    const sectorDescs = data.map((row) => row.desc);
    const firstRowIlo = data[0].ilo || {};
    const iloKeys = Object.keys(firstRowIlo);
    
    // Determine if it's sex-based or age-based
    const isSexBased = iloKeys.includes('male') || iloKeys.includes('female');
    
    let xCategories: string[] = [];
    let rawMatrix: number[][] = [];

    if (isSexBased) {
      xCategories = [
        'Output Change %',
        'Male Count',
        'Female Count',
        'Total Count',
        'Male Change',
        'Female Change',
        'Total Change'
      ];

      rawMatrix = data.map((row) => {
        const ilo = row.ilo || {};
        const maleCount = parseFloat(ilo.male?.count || 0);
        const femaleCount = parseFloat(ilo.female?.count || 0);
        const maleChange = parseFloat(ilo.male?.change || 0);
        const femaleChange = parseFloat(ilo.female?.change || 0);
        
        return [
          parseFloat(row.delta_output_percentage || 0),
          maleCount,
          femaleCount,
          maleCount + femaleCount,
          maleChange,
          femaleChange,
          maleChange + femaleChange
        ];
      });
    } else {
      // Age-based - sort Count first, then Change
      xCategories = ['Output Change %'];
      iloKeys.forEach((key) => xCategories.push(`${key} Count`));
      xCategories.push('Total Count');
      iloKeys.forEach((key) => xCategories.push(`${key} Change`));
      xCategories.push('Total Change');

      rawMatrix = data.map((row) => {
        const ilo = row.ilo || {};
        const rowData = [parseFloat(row.delta_output_percentage || 0)];
        
        let totalCount = 0;
        iloKeys.forEach((key) => {
          const val = parseFloat(ilo[key]?.count || 0);
          rowData.push(val);
          totalCount += val;
        });
        rowData.push(totalCount);

        let totalChange = 0;
        iloKeys.forEach((key) => {
          const val = parseFloat(ilo[key]?.change || 0);
          rowData.push(val);
          totalChange += val;
        });
        rowData.push(totalChange);

        return rowData;
      });
    }

    const numCols = xCategories.length;
    const colStats = Array.from({ length: numCols }, (_, x) => {
      const colValues = rawMatrix.map(row => row[x]);
      return {
        min: Math.min(...colValues),
        max: Math.max(...colValues)
      };
    });

    const RED = '#c4463a';
    const YELLOW = '#f6f921';
    const GREEN = '#007104';
    const WHITE = '#ffffff';

    const heatmapData: any[] = [];
    rawMatrix.forEach((row, yIndex) => {
      row.forEach((realVal, xIndex) => {
        const colHeader = xCategories[xIndex];
        const { min, max } = colStats[xIndex];
        
        let cellColor = WHITE;
        if (colHeader.includes('Output')) {
          if (realVal < 0) cellColor = interpolateColor(WHITE, RED, min < 0 ? realVal / min : 0);
          else if (realVal > 0) cellColor = interpolateColor(WHITE, GREEN, max > 0 ? realVal / max : 0);
        } else if (colHeader.includes('Count')) {
          const ratio = max > min ? (realVal - min) / (max - min) : 0.5;
          if (ratio < 0.5) cellColor = interpolateColor(GREEN, YELLOW, ratio * 2);
          else cellColor = interpolateColor(YELLOW, RED, (ratio - 0.5) * 2);
        } else if (colHeader.includes('Change')) {
          if (realVal < 0) cellColor = interpolateColor(WHITE, RED, min < 0 ? realVal / min : 0);
          else if (realVal > 0) cellColor = interpolateColor(WHITE, GREEN, max > 0 ? realVal / max : 0);
          else cellColor = WHITE;
        }

        let percentage: number | null = null;
        if (!colHeader.includes('%')) {
          const suffix = colHeader.includes('Count') ? 'Count' : 'Change';
          const totalColHeader = `Total ${suffix}`;
          const totalXIndex = xCategories.indexOf(totalColHeader);
          if (totalXIndex !== -1 && totalXIndex !== xIndex) {
            const totalVal = row[totalXIndex];
            if (totalVal !== 0) percentage = (realVal / totalVal) * 100;
          }
        }

        heatmapData.push({
          x: xIndex,
          y: yIndex,
          value: realVal,
          color: cellColor,
          realValue: realVal,
          percentage: percentage
        });
      });
    });

    return {
      chart: {
        type: 'heatmap',
        marginTop: 100,
        marginBottom: 120,
        plotBorderWidth: 1,
        height: 1000
      },

      title: { text: '' },
      subtitle: { text: '' },
      xAxis: [{
        categories: xCategories,
        opposite: true,
        labels: {
          rotation: -45,
          align: 'left',
          style: { 
            fontSize: '0.75rem',
            whiteSpace: 'normal',
            width: 100
          }
        }
      }, {

        categories: xCategories,
        linkedTo: 0,
        opposite: false,
        labels: {
          rotation: -45,
          align: 'right',
          style: { 
            fontSize: '0.75rem',
            whiteSpace: 'normal',
            width: 100
          }
        }
      }],


      yAxis: {
        categories: sectorCodes,
        title: null,
        reversed: true,
        labels: {
          style: {
            fontSize: '1rem'
          }
        }
      },
      credits: {
        enabled: false
      },
      legend: { enabled: false },

      tooltip: {
        useHTML: true,
        backgroundColor: '#ffffff',
        borderWidth: 0,
        shadow: true,
        formatter: function (this: any) {
          const p = this.point;
          const colName = this.series.xAxis.categories[p.x];
          const isPercentCol = colName.includes('%');
          
          let displayVal = formatCellNumber(p.realValue);
          if (displayVal !== '-') {
            displayVal += isPercentCol ? '%' : 'k';
          }

          let percentageRow = '';
          if (p.percentage !== null) {
            percentageRow = `
              <tr>
                <td style="padding: 2px 5px; color: #666">Share of Total:</td>
                <td style="padding: 2px 5px; text-align: right; color: #666"><b>${p.percentage.toFixed(2)}%</b></td>
              </tr>`;
          }

          return `
            <table style="border-collapse: collapse; border: none; font-size: 12px">
              <tr>
                <td style="padding: 2px 5px" colspan="2"><b>[${sectorCodes[p.y]}] ${sectorDescs[p.y]}</b></td>
              </tr>
              <tr style="border-top: 1px solid #eee">
                <td style="padding: 2px 5px">Indicator:</td>
                <td style="padding: 2px 5px; text-align: right"><b>${colName}</b></td>
              </tr>
              <tr>
                <td style="padding: 2px 5px">Value:</td>
                <td style="padding: 2px 5px; text-align: right"><b>${displayVal}</b></td>
              </tr>
              ${percentageRow}
            </table>`;
        }
      },
      series: [{
        name: 'Labour Impact',
        borderWidth: 0,
        data: heatmapData,
        dataLabels: {
          enabled: true,
          formatter: function (this: any) {
            const val = formatCellNumber(this.point.realValue);
            if (val === '-') return '-';
            return val + (this.series.xAxis.categories[this.point.x].includes('%') ? '%' : '');
          },
          style: {
            fontSize: '12px',
            textOutline: 'none',
            fontWeight: 'normal',
            color: 'contrast'
          }

        }
      }]
    };
  }, [data]);

  useEffect(() => {
    if (data && data.length > 0) {
      setIsLoading(false);
    }
  }, [data]);

  if (isLoading || !data || data.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status" variant="primary" className="me-3" />
        <div className="loading-text">
            {(!data || data.length === 0) ? 'No simulation results available' : 'Loading Heatmap...'}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5">
      <div className="overflow-auto rounded bg-white">
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
        />
      </div>
      <div className="mt-4 p-4 rounded border">
        <h6 className="fw-bold mb-3 fs-8 text-uppercase text-gray-700">Heatmap Color Guide</h6>
        <div className="row g-3">
          <div className="col-md-4">
            <div className="fw-bold fs-9 mb-1 text-primary">Simulation Baseline</div>
            <div className="d-flex align-items-center fs-9 text-muted">
              <span className="badge p-1 me-2" style={{background: 'linear-gradient(to right, #c4463a, #ffffff, #007104)', width: '60px', height: '10px'}}>&nbsp;</span>
              Red (-) &bull; White (0) &bull; Green (+)
            </div>
          </div>
          <div className="col-md-4 border-start">
            <div className="fw-bold fs-9 mb-1 text-primary">Workforce Concentration</div>
            <div className="d-flex align-items-center fs-9 text-muted">
              <span className="badge p-1 me-2" style={{background: 'linear-gradient(to right, #007104, #f6f921, #c4463a)', width: '60px', height: '10px'}}>&nbsp;</span>
              Green (Low) &bull; Yellow &bull; Red (High)
            </div>
          </div>
          <div className="col-md-4 border-start">
            <div className="fw-bold fs-9 mb-1 text-primary">Workforce Impact</div>
            <div className="d-flex align-items-center fs-9 text-muted">
              <span className="badge p-1 me-2" style={{background: 'linear-gradient(to right, #c4463a, #ffffff, #007104)', width: '60px', height: '10px'}}>&nbsp;</span>
              Red (Loss) &bull; White (0) &bull; Green (Gain)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};



export default LabourImpactHeatmap;
